import React, { Component } from "react";
import { connect } from "react-redux";
import { Formik, Form, Field } from "formik";
import { Button, Message, Grid, Divider } from "semantic-ui-react";

import FormsInputPassword from "../../inputs/password/forms_inputs_password";
import FormsInputsOtp from "../../inputs/otp/forms_inputs_otp";
import OtpResendControl from "../../login/otp/resend_control";
import LoginOtpSchema from "../../login/otp/validate";
import ChangePasswordSchema from "./validate";
import {
  requestPasswordChangeOtp,
  resendPasswordChangeOtp,
  changeAdminPassword,
  doLogout,
} from "../../../../actions";
import { getErrorMessage, getErrorCode } from "../../../../utils/api_error";

// Two steps in one panel: prove the current password, then confirm an emailed
// code. The pending challenge stays in component state so it is never written
// to the persisted Redux store.
class FormsUserPassword extends Component {
  state = {
    step: "credentials",
    pendingToken: null,
    otpPrefix: "",
    maskedEmail: "",
    // Set once the password has actually changed; the session is dead at that
    // point, so the only thing left to do is sign in again.
    done: false,
    error: false,
    errorMessage: "",
    notice: "",
  };

  restart = (notice) => {
    this.setState({
      step: "credentials",
      pendingToken: null,
      otpPrefix: "",
      maskedEmail: "",
      error: false,
      errorMessage: "",
      notice: notice || "",
    });
  };

  handleResent = (data) => {
    if (data && data.otpPrefix) {
      this.setState({ otpPrefix: data.otpPrefix });
    }
  };

  signOut = () => {
    this.props.doLogout();
    window.location.href = "/";
  };

  renderDone() {
    return (
      <Grid container columns={1}>
        <Grid.Column width={16}>
          <Divider horizontal>Change Password</Divider>
        </Grid.Column>
        <Grid.Column width={16}>
          <Message positive>
            <Message.Header>Password changed</Message.Header>
            <p>
              For your security, every signed-in session has been ended. Please
              sign in again with your new password.
            </p>
          </Message>
          <Button primary fluid onClick={this.signOut}>
            Go to Login
          </Button>
        </Grid.Column>
      </Grid>
    );
  }

  renderCredentialsStep() {
    const { user } = this.props;
    return (
      <Formik
        initialValues={{
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        }}
        onSubmit={(values, actions) => {
          this.setState({ error: false, errorMessage: "", notice: "" });
          this.props.requestPasswordChangeOtp(
            {
              accessToken: user.accessToken,
              currentPassword: values.currentPassword,
            },
            (result, error) => {
              actions.setSubmitting(false);
              if (error) {
                this.setState({
                  error: true,
                  errorMessage: getErrorMessage(
                    error,
                    "We could not start the password change. Please try again."
                  ),
                });
                return;
              }
              // Hold the new password in component state only while the code is
              // confirmed - it is never sent until the OTP is spent.
              this.pendingNewPassword = values.newPassword;
              this.setState({
                step: "otp",
                pendingToken: result.pendingToken,
                otpPrefix: result.otpPrefix,
                maskedEmail: result.maskedEmail,
              });
            }
          );
        }}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={ChangePasswordSchema}
        render={({ isSubmitting }) => (
          <Form>
            <Grid container columns={2}>
              <Grid.Column width={16}>
                <Divider horizontal>Change Password</Divider>
              </Grid.Column>
              <Grid.Column width={16}>
                <Field
                  name="currentPassword"
                  label="Current Password"
                  component={FormsInputPassword}
                  secondaryLabel=""
                />
              </Grid.Column>
              <Grid.Column>
                <Field
                  name="newPassword"
                  label="New Password"
                  component={FormsInputPassword}
                  secondaryLabel=""
                />
              </Grid.Column>
              <Grid.Column>
                <Field
                  name="confirmPassword"
                  label="Confirm New Password"
                  component={FormsInputPassword}
                  secondaryLabel=""
                />
              </Grid.Column>
              <Grid.Column width={16}>
                <Button
                  type="submit"
                  primary
                  fluid
                  loading={isSubmitting}
                  disabled={isSubmitting}>
                  Change Password
                </Button>
              </Grid.Column>
              <Grid.Column width={16}>
                {this.state.notice && (
                  <Message warning>{this.state.notice}</Message>
                )}
                {this.state.error && (
                  <Message negative>{this.state.errorMessage}</Message>
                )}
              </Grid.Column>
            </Grid>
          </Form>
        )}
      />
    );
  }

  renderOtpStep() {
    const { user } = this.props;
    const { pendingToken, otpPrefix, maskedEmail } = this.state;
    return (
      <Formik
        initialValues={{ otpDigits: "" }}
        onSubmit={(values, actions) => {
          this.setState({ error: false, errorMessage: "" });
          this.props.changeAdminPassword(
            {
              accessToken: user.accessToken,
              pendingToken,
              // Joined, with no dash - the dash exists only in the email.
              otpCode: `${otpPrefix}${values.otpDigits}`,
              newPassword: this.pendingNewPassword,
            },
            (result, error) => {
              if (error) {
                const errorCode = getErrorCode(error);
                const errorMessage = getErrorMessage(
                  error,
                  "That code was not accepted. Request a new one below."
                );
                // The challenge is gone - only re-entering the password can
                // start a new one.
                if (
                  errorCode === "OTP_MAX_ATTEMPTS" ||
                  errorCode === "SESSION_INVALID" ||
                  errorCode === "CHALLENGE_EXPIRED"
                ) {
                  this.pendingNewPassword = null;
                  this.restart(errorMessage);
                  return;
                }
                actions.setFieldValue("otpDigits", "");
                actions.setSubmitting(false);
                this.setState({ error: true, errorMessage });
                return;
              }
              this.pendingNewPassword = null;
              this.setState({ done: true });
            }
          );
        }}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={LoginOtpSchema}
        render={({ isSubmitting }) => (
          <Form>
            <Grid container columns={1}>
              <Grid.Column width={16}>
                <Divider horizontal>Confirm Password Change</Divider>
              </Grid.Column>
              <Grid.Column width={16}>
                <Field
                  name="otpDigits"
                  label=""
                  component={FormsInputsOtp}
                  prefix={`${otpPrefix}-`}
                  secondaryLabel={
                    maskedEmail
                      ? `Enter the 4 digits from the code sent to ${maskedEmail}`
                      : "Enter the 4 digits from the code sent to your email"
                  }
                />
                &nbsp;
                <Button
                  type="submit"
                  primary
                  fluid
                  loading={isSubmitting}
                  disabled={isSubmitting}>
                  Confirm and Change Password
                </Button>
                <OtpResendControl
                  pendingToken={pendingToken}
                  resend={(token, cb) =>
                    this.props.resendPasswordChangeOtp(
                      { accessToken: user.accessToken, pendingToken: token },
                      cb
                    )
                  }
                  onResent={this.handleResent}
                  onSessionLost={this.restart}
                />
                <Button
                  type="button"
                  basic
                  fluid
                  size="small"
                  style={{ marginTop: "1em", padding: 0, boxShadow: "none" }}
                  onClick={() => {
                    this.pendingNewPassword = null;
                    this.restart("");
                  }}>
                  Cancel
                </Button>
                {this.state.error && (
                  <Message negative>{this.state.errorMessage}</Message>
                )}
              </Grid.Column>
            </Grid>
          </Form>
        )}
      />
    );
  }

  render() {
    if (this.state.done) {
      return this.renderDone();
    }
    return this.state.step === "otp"
      ? this.renderOtpStep()
      : this.renderCredentialsStep();
  }
}

export default connect(null, {
  requestPasswordChangeOtp,
  resendPasswordChangeOtp,
  changeAdminPassword,
  doLogout,
})(FormsUserPassword);
